/**
 * Represents an anchor position on the border of a class.
 *
 * The anchor can be specified either as absolute coordinates (x, y)
 * or as a side + offset combination for more intuitive positioning.
 */

export type AnchorSide = 'top' | 'right' | 'bottom' | 'left';

export interface AnchorPosition {
    // Absolute position on the class border
    x: number;
    y: number;
    // Which side of the class the anchor is on
    side: AnchorSide;
    // Offset along that side (0 = start, 1 = end)
    offset: number;
}

/**
 * Computes the absolute position of an anchor given a class position and dimensions.
 */
export function getAnchorAbsolutePosition(
    anchor: AnchorPosition,
    classX: number,
    classY: number,
    classWidth: number,
    classHeight: number
): { x: number; y: number } {
    switch (anchor.side) {
        case 'top':
            return { x: classX + anchor.offset * classWidth, y: classY };
        case 'right':
            return { x: classX + classWidth, y: classY + anchor.offset * classHeight };
        case 'bottom':
            return { x: classX + anchor.offset * classWidth, y: classY + classHeight };
        case 'left':
            return { x: classX, y: classY + anchor.offset * classHeight };
    }
}

/**
 * Constrains a point to the nearest position on the class border.
 * Returns an AnchorPosition with the side and offset.
 */
export function constrainToClassBorder(
    pointX: number,
    pointY: number,
    classX: number,
    classY: number,
    classWidth: number,
    classHeight: number
): AnchorPosition {
    // Calculate center of the class
    const centerX = classX + classWidth / 2;
    const centerY = classY + classHeight / 2;

    // Calculate angle from center to point
    const angle = Math.atan2(pointY - centerY, pointX - centerX);

    // Determine which side of the rectangle the point should be constrained to
    // based on the angle
    const absAngle = Math.abs(angle);

    let side: AnchorSide;
    let x: number;
    let y: number;
    let offset: number;

    // The diagonal angle determines which sides we compare
    const diagonalAngle = Math.atan(classHeight / classWidth);

    if (absAngle < diagonalAngle) {
        // Right side
        side = 'right';
        x = classX + classWidth;
        // Clamp y to the class bounds
        y = Math.max(classY, Math.min(classY + classHeight, pointY));
        offset = (y - classY) / classHeight;
    } else if (absAngle > Math.PI - diagonalAngle) {
        // Left side
        side = 'left';
        x = classX;
        // Clamp y to the class bounds
        y = Math.max(classY, Math.min(classY + classHeight, pointY));
        offset = (y - classY) / classHeight;
    } else if (angle > 0) {
        // Bottom side
        side = 'bottom';
        y = classY + classHeight;
        // Clamp x to the class bounds
        x = Math.max(classX, Math.min(classX + classWidth, pointX));
        offset = (x - classX) / classWidth;
    } else {
        // Top side
        side = 'top';
        y = classY;
        // Clamp x to the class bounds
        x = Math.max(classX, Math.min(classX + classWidth, pointX));
        offset = (x - classX) / classWidth;
    }

    return { x, y, side, offset };
}

export type AnchorMode = 'auto' | 'dynamic' | 'centered';

/**
 * Computes the default anchor position based on the selected anchor mode.
 * @param mode - 'auto': Original bounding-box logic, 'dynamic': center-to-center intersection, 'centered': edge midpoint snap
 */
export function computeDefaultAnchor(
    sourceClass: { x: number; y: number; width: number; height: number },
    targetClass: { x: number; y: number; width: number; height: number },
    isSource: boolean,
    mode: AnchorMode = 'auto'
): AnchorPosition {
    switch (mode) {
        case 'centered':
            return computeCenteredAnchor(sourceClass, targetClass, isSource);
        case 'dynamic':
            return computeDynamicAnchor(sourceClass, targetClass, isSource);
        case 'auto':
        default:
            return computeAutoAnchor(sourceClass, targetClass, isSource);
    }
}

/**
 * Dynamic anchor mode: Uses center-to-center line intersection.
 * Anchors point directly toward the other class's center.
 * Provides smooth continuous movement as classes move - no jumps.
 */
function computeDynamicAnchor(
    sourceClass: { x: number; y: number; width: number; height: number },
    targetClass: { x: number; y: number; width: number; height: number },
    isSource: boolean
): AnchorPosition {
    const thisClass = isSource ? sourceClass : targetClass;
    const otherClass = isSource ? targetClass : sourceClass;

    const thisCenterX = thisClass.x + thisClass.width / 2;
    const thisCenterY = thisClass.y + thisClass.height / 2;
    const otherCenterX = otherClass.x + otherClass.width / 2;
    const otherCenterY = otherClass.y + otherClass.height / 2;

    // Direction from this class center to other class center
    const dx = otherCenterX - thisCenterX;
    const dy = otherCenterY - thisCenterY;

    // Handle identical centers - default to right side
    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
        return {
            x: thisClass.x + thisClass.width,
            y: thisCenterY,
            side: 'right',
            offset: 0.5
        };
    }

    // Find intersection with class border using parametric line equation
    // Line: P = thisCenter + t * (otherCenter - thisCenter)
    // We need the smallest positive t that puts P on the border

    let side: AnchorSide;
    let x: number;
    let y: number;
    let offset: number;

    // Calculate t for each side
    const tRight = dx !== 0 ? (thisClass.x + thisClass.width - thisCenterX) / dx : Infinity;
    const tLeft = dx !== 0 ? (thisClass.x - thisCenterX) / dx : Infinity;
    const tBottom = dy !== 0 ? (thisClass.y + thisClass.height - thisCenterY) / dy : Infinity;
    const tTop = dy !== 0 ? (thisClass.y - thisCenterY) / dy : Infinity;

    // Find the smallest positive t (first intersection going toward other class)
    const minT = Infinity;
    const candidates: { t: number; side: AnchorSide }[] = [];

    if (tRight > 0 && tRight < minT) candidates.push({ t: tRight, side: 'right' });
    if (tLeft > 0 && tLeft < minT) candidates.push({ t: tLeft, side: 'left' });
    if (tBottom > 0 && tBottom < minT) candidates.push({ t: tBottom, side: 'bottom' });
    if (tTop > 0 && tTop < minT) candidates.push({ t: tTop, side: 'top' });

    // Sort by t and pick the smallest
    candidates.sort((a, b) => a.t - b.t);
    const winner = candidates[0];

    if (!winner) {
        // Fallback - shouldn't happen
        return { x: thisCenterX, y: thisCenterY, side: 'right', offset: 0.5 };
    }

    const t = winner.t;
    // eslint-disable-next-line prefer-const
    side = winner.side;
    // eslint-disable-next-line prefer-const
    x = thisCenterX + t * dx;
    // eslint-disable-next-line prefer-const
    y = thisCenterY + t * dy;

    // Calculate offset along the edge
    switch (side) {
        case 'top':
        case 'bottom':
            offset = (x - thisClass.x) / thisClass.width;
            break;
        case 'left':
        case 'right':
            offset = (y - thisClass.y) / thisClass.height;
            break;
    }

    // Clamp offset to valid range
    offset = Math.max(0, Math.min(1, offset));

    return { x, y, side, offset };
}

/**
 * Centered anchor mode: Anchors snap to the midpoint of the nearest edge.
 * Only 4 possible positions per class (offset always 0.5).
 * Simplest and most predictable visual appearance.
 */
function computeCenteredAnchor(
    sourceClass: { x: number; y: number; width: number; height: number },
    targetClass: { x: number; y: number; width: number; height: number },
    isSource: boolean
): AnchorPosition {
    const thisClass = isSource ? sourceClass : targetClass;
    const otherClass = isSource ? targetClass : sourceClass;

    const thisCenterX = thisClass.x + thisClass.width / 2;
    const thisCenterY = thisClass.y + thisClass.height / 2;
    const otherCenterX = otherClass.x + otherClass.width / 2;
    const otherCenterY = otherClass.y + otherClass.height / 2;

    // Calculate angle from this class center to other class center
    const angle = Math.atan2(otherCenterY - thisCenterY, otherCenterX - thisCenterX);

    // Determine which edge faces the other class based on angle
    // Use diagonal angles of a square to divide into 4 quadrants
    const diagonalAngle = Math.PI / 4; // 45 degrees

    let side: AnchorSide;
    let x: number;
    let y: number;

    if (angle > -diagonalAngle && angle <= diagonalAngle) {
        // Other is to the right
        side = 'right';
        x = thisClass.x + thisClass.width;
        y = thisCenterY;
    } else if (angle > diagonalAngle && angle <= Math.PI - diagonalAngle) {
        // Other is below
        side = 'bottom';
        x = thisCenterX;
        y = thisClass.y + thisClass.height;
    } else if (angle > -Math.PI + diagonalAngle && angle <= -diagonalAngle) {
        // Other is above
        side = 'top';
        x = thisCenterX;
        y = thisClass.y;
    } else {
        // Other is to the left
        side = 'left';
        x = thisClass.x;
        y = thisCenterY;
    }

    return { x, y, side, offset: 0.5 };
}

/**
 * Auto anchor mode (original implementation):
 * Uses bounding box logic to ensure relationship lines don't cross through class boxes:
 * - Use horizontal connection (left/right anchors) when classes don't overlap horizontally
 * - Use vertical connection (top/bottom anchors) when classes overlap horizontally but not vertically
 */
function computeAutoAnchor(
    sourceClass: { x: number; y: number; width: number; height: number },
    targetClass: { x: number; y: number; width: number; height: number },
    isSource: boolean
): AnchorPosition {
    // Minimum gap required to prefer vertical over horizontal connections
    const MIN_GAP_FOR_VERTICAL = 30;

    // Calculate bounding box edges
    const sourceLeft = sourceClass.x;
    const sourceRight = sourceClass.x + sourceClass.width;
    const sourceTop = sourceClass.y;
    const sourceBottom = sourceClass.y + sourceClass.height;
    const sourceCenterY = sourceClass.y + sourceClass.height / 2;
    const sourceCenterX = sourceClass.x + sourceClass.width / 2;

    const targetLeft = targetClass.x;
    const targetRight = targetClass.x + targetClass.width;
    const targetTop = targetClass.y;
    const targetBottom = targetClass.y + targetClass.height;
    const targetCenterY = targetClass.y + targetClass.height / 2;
    const targetCenterX = targetClass.x + targetClass.width / 2;

    // Calculate vertical gap between classes
    const verticalGap = Math.max(0, Math.max(targetTop - sourceBottom, sourceTop - targetBottom));

    // Determine if classes are separated horizontally or vertically with sufficient gap
    const sourceFullyLeftOfTarget = sourceRight < targetLeft;
    const sourceFullyRightOfTarget = sourceLeft > targetRight;
    const sourceFullyAboveTarget = sourceBottom < targetTop;
    const sourceFullyBelowTarget = sourceTop > targetBottom;

    // Horizontal separation - use left/right anchors
    const hasHorizontalSeparation = sourceFullyLeftOfTarget || sourceFullyRightOfTarget;
    // Vertical separation with sufficient gap - use top/bottom anchors
    const hasVerticalSeparation = sourceFullyAboveTarget || sourceFullyBelowTarget;

    // Prefer horizontal if there's horizontal separation, unless vertical gap is significantly larger
    const useHorizontal = hasHorizontalSeparation;
    // Only use vertical if there's vertical separation AND (no horizontal separation OR vertical gap is large enough)
    const useVertical = hasVerticalSeparation && (!hasHorizontalSeparation || verticalGap >= MIN_GAP_FOR_VERTICAL);

    if (isSource) {
        // Source anchor
        if (useHorizontal && !useVertical) {
            // Classes are horizontally separated - use left/right
            if (sourceFullyLeftOfTarget) {
                // Source is left of target - anchor on right side of source
                const y = Math.max(Math.min(targetCenterY, sourceBottom - sourceClass.height * 0.125), sourceTop + sourceClass.height * 0.125);
                const offset = (y - sourceTop) / sourceClass.height;
                return { x: sourceRight, y, side: 'right', offset };
            } else {
                // Source is right of target - anchor on left side of source
                const y = Math.max(Math.min(targetCenterY, sourceBottom - sourceClass.height * 0.125), sourceTop + sourceClass.height * 0.125);
                const offset = (y - sourceTop) / sourceClass.height;
                return { x: sourceLeft, y, side: 'left', offset };
            }
        } else if (useVertical) {
            // Classes are vertically separated - use top/bottom
            if (sourceFullyAboveTarget) {
                // Source is above target - anchor on bottom side of source
                const x = Math.max(Math.min(targetCenterX, sourceRight - sourceClass.width * 0.125), sourceLeft + sourceClass.width * 0.125);
                const offset = (x - sourceLeft) / sourceClass.width;
                return { x, y: sourceBottom, side: 'bottom', offset };
            } else {
                // Source is below target - anchor on top side of source
                const x = Math.max(Math.min(targetCenterX, sourceRight - sourceClass.width * 0.125), sourceLeft + sourceClass.width * 0.125);
                const offset = (x - sourceLeft) / sourceClass.width;
                return { x, y: sourceTop, side: 'top', offset };
            }
        } else {
            // Classes overlap - fallback to angle-based logic for edge cases
            const angle = Math.atan2(targetCenterY - sourceCenterY, targetCenterX - sourceCenterX);
            if (angle > -Math.PI/4 && angle <= Math.PI/4) {
                // Target center is to the right
                const y = Math.max(Math.min(targetCenterY, sourceBottom - sourceClass.height * 0.125), sourceTop + sourceClass.height * 0.125);
                const offset = (y - sourceTop) / sourceClass.height;
                return { x: sourceRight, y, side: 'right', offset };
            } else if (angle > Math.PI/4 && angle <= 3*Math.PI/4) {
                // Target center is below
                const x = Math.max(Math.min(targetCenterX, sourceRight - sourceClass.width * 0.125), sourceLeft + sourceClass.width * 0.125);
                const offset = (x - sourceLeft) / sourceClass.width;
                return { x, y: sourceBottom, side: 'bottom', offset };
            } else if (angle > -3*Math.PI/4 && angle <= -Math.PI/4) {
                // Target center is above
                const x = Math.max(Math.min(targetCenterX, sourceRight - sourceClass.width * 0.125), sourceLeft + sourceClass.width * 0.125);
                const offset = (x - sourceLeft) / sourceClass.width;
                return { x, y: sourceTop, side: 'top', offset };
            } else {
                // Target center is to the left
                const y = Math.max(Math.min(targetCenterY, sourceBottom - sourceClass.height * 0.125), sourceTop + sourceClass.height * 0.125);
                const offset = (y - sourceTop) / sourceClass.height;
                return { x: sourceLeft, y, side: 'left', offset };
            }
        }
    } else {
        // Target anchor
        if (useHorizontal && !useVertical) {
            // Classes are horizontally separated - use left/right
            if (sourceFullyLeftOfTarget) {
                // Source is left of target - anchor on left side of target
                const y = Math.max(Math.min(sourceCenterY, targetBottom - targetClass.height * 0.125), targetTop + targetClass.height * 0.125);
                const offset = (y - targetTop) / targetClass.height;
                return { x: targetLeft, y, side: 'left', offset };
            } else {
                // Source is right of target - anchor on right side of target
                const y = Math.max(Math.min(sourceCenterY, targetBottom - targetClass.height * 0.125), targetTop + targetClass.height * 0.125);
                const offset = (y - targetTop) / targetClass.height;
                return { x: targetRight, y, side: 'right', offset };
            }
        } else if (useVertical) {
            // Classes are vertically separated - use top/bottom
            if (sourceFullyAboveTarget) {
                // Source is above target - anchor on top side of target
                const x = Math.max(Math.min(sourceCenterX, targetRight - targetClass.width * 0.125), targetLeft + targetClass.width * 0.125);
                const offset = (x - targetLeft) / targetClass.width;
                return { x, y: targetTop, side: 'top', offset };
            } else {
                // Source is below target - anchor on bottom side of target
                const x = Math.max(Math.min(sourceCenterX, targetRight - targetClass.width * 0.125), targetLeft + targetClass.width * 0.125);
                const offset = (x - targetLeft) / targetClass.width;
                return { x, y: targetBottom, side: 'bottom', offset };
            }
        } else {
            // Classes overlap - fallback to angle-based logic
            const angle = Math.atan2(targetCenterY - sourceCenterY, targetCenterX - sourceCenterX);
            if (angle > -Math.PI/4 && angle <= Math.PI/4) {
                // Source is to the left - anchor on left side of target
                return { x: targetLeft, y: targetCenterY, side: 'left', offset: 0.5 };
            } else if (angle > Math.PI/4 && angle <= 3*Math.PI/4) {
                // Source is above - anchor on top side of target
                return { x: targetCenterX, y: targetTop, side: 'top', offset: 0.5 };
            } else if (angle > -3*Math.PI/4 && angle <= -Math.PI/4) {
                // Source is below - anchor on bottom side of target
                return { x: targetCenterX, y: targetBottom, side: 'bottom', offset: 0.5 };
            } else {
                // Source is to the right - anchor on right side of target
                return { x: targetRight, y: targetCenterY, side: 'right', offset: 0.5 };
            }
        }
    }
}

export default AnchorPosition;
